import { Check, Eye, Send } from 'lucide-react'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { ErrorState } from '@/components/feedback/ErrorState'
import { Banner } from '@/components/feedback/Banner'
import { Skeleton } from '@/components/feedback/Skeleton'
import { Button } from '@/components/ui/Button'
import { QuoteStatusPill } from '@/components/ui/StatusPill'
import { TabNav, type TabNavItem } from '@/components/ui/TabNav'
import { TabPanel } from '@/components/ui/TabPanel'
import { placeName } from '@/lib/countries'
import { MODE_ICONS } from '@/lib/icons'
import {
  useQuote,
  useQuoteTeam,
  useConvertQuote,
  useReopenQuote,
  useOfferPreview,
  type OfferLanguage,
  useSendQuote,
  useUpdateQuote,
  toQuoteUpdatePayload,
} from '@/features/quotes/api/useQuote'
import { useSession } from '@/features/auth/api/useSession'
import { ChatDock, ChatDockBadge } from '@/features/chat/components/ChatDock'
import {
  canSeeRecordChat,
  useRecordConversation,
} from '@/features/chat/api/useRecordChat'
import { MarginPanel } from '@/features/quotes/components/MarginPanel'
import { OfferPreviewSheet } from '@/features/quotes/components/OfferPreviewSheet'
import { OfferSendSheet } from '@/features/quotes/components/OfferSendSheet'
import { QuoteFormSheet } from '@/features/quotes/components/QuoteFormSheet'
import { QuoteHistorySection } from '@/features/quotes/components/QuoteHistorySection'
import { QuoteTeamSection } from '@/features/quotes/components/QuoteTeamSection'
import {
  CargoSection,
  CostLineSection,
  RevisionsSection,
  RouteOptionsSection,
  TermsSection,
} from '@/features/quotes/components/QuoteSections'
import {
  QuotePage,
  QuotePageColumns,
  QuotePageHeader,
} from '@/features/quotes/components/QuotePageChrome'
import {
  QuoteMoneyCard,
  QuoteOfferFactsCard,
  QuoteReadinessCard,
} from '@/features/quotes/components/QuoteAsideCards'
import {
  errorMessage,
  issueText,
  sendBlockedReason,
  sendErrorMessage,
} from '@/features/quotes/quoteText'
import type { CargoPosition, CostLine } from '@/types/domain'
import { useTranslation } from 'react-i18next'

/* `errorMessage` used to be defined here, identically to the copy in
   quoteText. That local copy is what kept this page on the generic wording:
   `sendErrorMessage` was extracted precisely so the branding refusal could name
   Settings → Entities, and nothing imported it, because the name it would have
   shadowed was already in scope. Imported now, and the duplicate is gone. */

/* The sections are the SHARED ones (QuoteSections), not copies.
 *
 * `CargoSection`, `RouteOptionsSection`, `RevisionsSection` and the header's
 * `TotalFigure` all used to be defined here as well, beside the identical
 * exports the builder imports — and they had already drifted: the copy of the
 * cargo table on this page had lost the tint under its totals row, so the same
 * table read as four positions on one surface and three plus a footer on the
 * other. `issueText` was a third copy of the same sentence.
 *
 * Deleted rather than re-synced, exactly as the costing and the terms were
 * before them. The two quote surfaces are one implementation hosted twice, and
 * that is only true while nothing on either of them is a local re-statement of
 * the other.
 *
 * `HistorySection` was the last one still defined here, together with the action
 * catalogue and the wording helper it needed, and it is now
 * `QuoteHistorySection` beside its neighbours. It was the only reason this page
 * imported a query hook, a timestamp formatter and four Card parts it uses
 * nowhere else — which is what a section left behind on a page always costs. */

/* The detail page's sections, in the order the work happens: what is being
 * shipped, what it costs, how it travels, on what terms — then the two
 * backward-looking ones. Cargo is first and is the default, because a quote
 * that cannot be sent is nearly always a quote whose cargo is incomplete. */
const TABS = [
  { key: 'cargo', labelKey: 'quotes.tabs.cargo' },
  { key: 'pricing', labelKey: 'quotes.tabs.pricing' },
  { key: 'routes', labelKey: 'quotes.tabs.routes' },
  /* The parties sit between the routing and the terms: they are who the cargo
     passes through, which is a question about the shipment rather than about
     the deal — and the wizard asks for them on the same step as the terms only
     because that step is also the create. */
  { key: 'participants', labelKey: 'quotes.tabs.participants' },
  { key: 'terms', labelKey: 'quotes.tabs.terms' },
  { key: 'revisions', labelKey: 'quotes.tabs.revisions' },
  { key: 'history', labelKey: 'quotes.tabs.history' },
] as const satisfies readonly TabNavItem<string>[]

type TabKey = (typeof TABS)[number]['key']

export function QuoteDetailPage() {
  const { t, i18n } = useTranslation()
  const { quoteId = '' } = useParams()
  const { data: record, isPending, isError, error, refetch } = useQuote(quoteId)
  // Declared before the early returns: hooks cannot be called conditionally,
  // and the loading and error branches below both return.
  const [cargoTarget, setCargoTarget] = useState<CargoPosition | 'new' | null>(null)
  const [costLineTarget, setCostLineTarget] = useState<CostLine | 'new' | null>(null)
  const [sendOpen, setSendOpen] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  /* The CLIENT's language, for the offer — the one fact the preview sheet and
     the send sheet must agree on, which is why it is held here and not in
     either of them. Previewing in one language and issuing in another is the
     one way this pair of sheets can lie to a reader who checked the document
     before pressing send.

     Seeded from the INTERFACE, and this is a guess rather than a lookup: a
     quote records no language for its client, so the best available answer is
     that a manager working in English is probably quoting a client who reads
     it. The server's own default (Russian) still applies to anything that never
     reaches this page. */
  const [offerLanguage, setOfferLanguage] = useState<OfferLanguage>(
    i18n.language.startsWith('en') ? 'en' : 'ru',
  )
  const [editing, setEditing] = useState(false)
  /* The PHONE's full-screen conversation. Owned by the page rather than by the
     dock, because the badge that opens it lives in the header strip and the
     panel is mounted at the end of this tree — one piece of state with two
     readers beats an imperative handle between siblings. The desktop dock's
     own collapsed/expanded choice is not here: it is a remembered per-reader
     preference the dock owns, because it outlives this page (see ChatDock). */
  const [chatOpen, setChatOpen] = useState(false)
  /* The open section is a search param rather than local state, so a section is
     linkable and survives a refresh — the same contract the Settings and
     Directories switchers keep. An unknown or absent value falls back to Cargo
     rather than rendering an empty frame for a mistyped link. */
  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab')
  const tab: TabKey = TABS.some((item) => item.key === requested)
    ? (requested as TabKey)
    : 'cargo'
  const send = useSendQuote(quoteId)
  /* Fetched only while the sheet is open: composing an offer reads the cargo,
     the costing and the routings server-side, and doing that on every visit to
     a quote would be a document nobody asked to see. */
  const preview = useOfferPreview(quoteId, {
    enabled: previewOpen,
    language: offerLanguage,
  })
  const update = useUpdateQuote()
  const reopen = useReopenQuote(quoteId)
  const convert = useConvertQuote()
  const navigate = useNavigate()

  /* CHAT ON A QUOTE (0051). The same panel the case detail renders, over the
     same service, on quote's own row scope — narrowed to the people staffed on
     this quote. Not rendered at all for anyone else: every request it made
     would answer 404, and a badge that opens onto an error is worse than no
     badge.
     The owner id IS handed over, and an empty string here was a bug: the quote's
     owner is seeded onto `quote_team` at create (migration 0028), but Unassign
     refuses only the removal that would EMPTY the team, so an owner can be taken
     off their own quote. StaffedOrOwned still serves them the conversation
     (`q.owner_id = $1 OR` the team predicate) and this page rendered nothing.
     Declared here, above the early returns: hooks cannot be called
     conditionally, and both the loading and the error branch below return —
     so `record` may be undefined for one render, which reads as "not yet". */
  const { data: user } = useSession()
  const team = useQuoteTeam(quoteId)
  const canChat = canSeeRecordChat(user, record?.ownerId ?? '', team.data ?? [])
  const conversation = useRecordConversation('quote', quoteId, canChat)

  if (isPending) {
    return (
      <div className="flex flex-col gap-4" aria-busy="true" aria-label={t('quotes.loading')}>
        <Skeleton className="h-20 w-full rounded-[var(--radius-DEFAULT)]" />
        <Skeleton className="h-16 w-full rounded-md" />
        <Skeleton className="h-64 w-full rounded-[var(--radius-DEFAULT)]" />
      </div>
    )
  }

  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  // An absent checklist is not a clean one: nothing has been checked because
  // nothing was entered, which is why cargo is asked about separately.
  const checklist = record.checklist ?? []
  const hasCargo = (record.cargo ?? []).length > 0
  const blocked = checklist.length > 0
  // The SERVER decides whether an offer may go out (FR-3). Falling back to the
  // local reading only for a response that predates the field — deriving it
  // here in general would let the client and the server disagree about the one
  // question this screen exists to answer.
  const sendable = record.sendable ?? (hasCargo && !blocked)
  // Reopening has no local fallback the way sendable does. Whether a status may
  // open a new revision is a lifecycle rule the client does not hold, so an
  // older server that does not send the field means the button stays off —
  // which is the safe direction, and the refusal would say so anyway.
  const reopenable = record.reopenable ?? false
  // What "Заполнить груз" opens: the position the FIRST listed issue is about,
  // because the banner lists them in a stable order and the top line is the one
  // the eye is on when the button is pressed. An issue about the quote as a
  // whole carries no positionId, and an issue can outlive the row it names if
  // the checklist is a render behind a deletion — either way there is no row to
  // open, so the sheet opens on a new position rather than on nothing at all,
  // which is the state the button was stuck in before.
  const firstBlockedId = checklist.find((issue) => issue.positionId)?.positionId
  const firstBlocked =
    (record.cargo ?? []).find((position) => position.id === firstBlockedId) ?? 'new'
  // Absent until a route option carries one (FR-3, Story 2.4): a header-only
  // quote has a client before it has a routing.
  const ModeIcon = record.mode ? MODE_ICONS[record.mode] : undefined

  return (
    /* ONE COLUMN, at every width — the same shape the case detail lays out, for
       the same reason. This used to be a flex ROW above lg with a persistent
       chat column on the right, which cost the quote roughly 306px of width on
       every screen. The dock floats over the corner now (0052) and is out of
       flow entirely: it is still a sibling below, but only for z-order — it
       takes part in no layout here and nothing is sized around it.

       QuotePage keeps its own `min-w-0`, which is what stops a wide costing
       table from scrolling the page sideways instead of scrolling inside its
       own card. Nothing here competes with it for width. */
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {/* The frame is the BUILDER's, shared through QuotePageChrome: the same
         bordered header strip, the same 1fr + 340px body and the same 3.5 gap.
         This page used to draw its own — a bare heading over a full-width column
         at a different gap — and the two routes onto one quote read as two
         products because of it. Nothing about what this page loads, or where its
         controls go, moved with the frame.

         The sheets stay INSIDE it, exactly where they were: they render fixed
         overlays and have no business becoming flex children of the chat row. */}
      <QuotePage>
        <QuotePageHeader
          backTo="/quotes"
          backLabel={t('quotes.backToList')}
          /* The number IS the title here, where the builder titles itself and
             chips the number: on this route the record exists and is what the
             reader came for, and there is no act in progress to name instead. */
          title={record.number}
          badges={<QuoteStatusPill status={record.status} />}
          subtitle={
            <>
              <span className="text-text-body">{record.clientName}</span>
              <span aria-hidden className="text-text-faint">·</span>
              {ModeIcon ? <ModeIcon size={14} aria-hidden /> : null}
              {record.origin && record.destination ? (
                <>
                  {placeName(record.origin, i18n.language)}
                  <span aria-hidden className="text-text-faint">→</span>
                  {placeName(record.destination, i18n.language)}
                </>
              ) : null}
              {record.pricingOwnerName ? (
                <>
                  <span aria-hidden className="text-text-faint">·</span>
                  Pricing: {record.pricingOwnerName}
                </>
              ) : null}
            </>
          }
          /* The record's actions where the builder puts its step rail. They stay
             in the header rather than moving to a footer bar: the builder's
             footer carries STEP navigation, which this page has none of, and a
             fixed bar holding Send would cover the section the reader is
             checking before they press it. */
          trailing={
            <>
              {/* The phone's way into the conversation, first in the row because
                  it is the one control here that is not about the quote's
                  lifecycle. `lg:hidden` inside it: above the breakpoint the
                  dock is its own way back in — expanded in the corner, or the
                  floating button carrying the same count this badge does
                  (UX-DR-28). */}
              {canChat ? (
                <ChatDockBadge
                  onOpen={() => setChatOpen(true)}
                  unread={conversation?.unread ?? 0}
                />
              ) : null}

              {/* A converted quote is READ-ONLY and offers the way into its case
                  (FR-19, FR-21). Shown first because once a case exists it is the
                  thing the reader came for — the quote behind it is provenance. */}
              {/* A button that NAVIGATES rather than a Link styled as one: this
                  Button renders a real <button> and has no asChild escape, and its
                  variant recipe is not exported for a Link to borrow. The
                  affordance and the destination are identical either way. */}
              {record.status === 'converted' && record.caseId ? (
                <Button
                  variant="secondary"
                  onClick={() => navigate(`/cases/${record.caseId}`)}
                >
                  {t('quotes.actions.openCase', {
                    number: record.caseNumber ?? '',
                  })}
                </Button>
              ) : null}

              {/* Approving is the act that creates the case (FR-19, AD-14): one
                  server transaction writes the case, its rate and its planned
                  lines, or none of them. Offered only on a quote the client can
                  still answer — a converted one already has its case above, and a
                  draft was never sent.

                  Not disabled-with-a-title like the reopen button beside it,
                  because this action is ABSENT rather than refused on states that
                  cannot convert: a Send button greyed out invites a second click,
                  while a missing Approve button says the question is not open. */}
              {record.status === 'sent' ? (
                <Button
                  disabled={convert.isPending}
                  onClick={() =>
                    convert.mutate(quoteId, {
                      // FR-21 in one gesture: the case is what the user wanted, so
                      // the screen goes there rather than reporting that it exists.
                      onSuccess: (result) => navigate(`/cases/${result.caseId}`),
                    })
                  }
                >
                  {convert.isPending
                    ? t('quotes.actions.converting')
                    : t('quotes.actions.approve')}
                </Button>
              ) : null}

              {/* EDIT THE HEADER (FR-4, FR-11, FR-59, Story 2.1).
                  PUT /quotes/:id has existed, been guarded and been action-logged
                  since Story 2.1, and until 2026-08-17 nothing in the product called
                  it: `useUpdateQuote` had no consumer and QuoteFormSheet was
                  rendered only by its own test. So a quote's client, validity, terms
                  and header measures could be set once, in the wizard, and never
                  corrected — and Story 2.6's "editing is only possible by opening a
                  new revision, which reopens the editing window" reopened a window
                  with no editor in it.

                  Enablement is the SERVER's `editable`, not a status comparison
                  written again here: the two would drift, and the refusal is what
                  ultimately decides (AD-21). A quote that is not editable keeps the
                  button HOVERABLE through blockedReason rather than taking a plain
                  `disabled`, because `disabled` makes a title unreachable by mouse
                  and by Tab — so the one sentence explaining why the user cannot
                  type would be the one sentence they cannot read. */}
              <Button
                variant="secondary"
                disabled={update.isPending}
                blockedReason={
                  record.editable === false
                    ? t('quotes.revisions.notEditable')
                    : undefined
                }
                onClick={() => {
                  update.reset()
                  setEditing(true)
                }}
              >
                {t('common.actions.edit')}
              </Button>

              {/* A new revision reopens the editing window on a Sent or Rejected
                  quote (FR-6, FR-7). The already-issued revisions are untouched,
                  which is what makes this safe to offer from a sent offer: what
                  reopens is the live header, and the history stays the record of
                  what the client actually received.

                  Enablement is the SERVER's answer, not a status check written
                  again here — the two would drift, and the refusal is what
                  ultimately decides. */}
              {/* The two refusals are NOT one condition. A rule the reader could
                  act on goes to blockedReason, which keeps the button hoverable so
                  the sentence can actually be read; a mutation in flight stays a
                  real `disabled`, because "Reopening…" on the button already says
                  why and a tooltip repeating it would be noise. */}
              <Button
                variant="secondary"
                disabled={reopen.isPending}
                blockedReason={
                  reopenable ? undefined : t('quotes.revisions.notReopenable')
                }
                onClick={() => reopen.mutate()}
              >
                {reopen.isPending
                  ? t('quotes.actions.reopening')
                  : t('quotes.actions.newRevision')}
              </Button>
              {/* Sending opens the sheet rather than firing immediately: the
                  recipient has to be on screen before an email leaves permanently
                  (FR-71), and the sheet is where the user sees it.

                  No cargo disables Send as firmly as an open issue does. The
                  checklist is built FROM the cargo, so an empty one on a quote with
                  no positions means nothing has been checked — not that everything
                  passed, which is the reading that would send an empty offer.

                  Which of the two it is gets SAID, on a button that stays hoverable
                  to say it. The checklist banner below names the individual issues,
                  but only on a quote that has cargo — an empty one has no banner at
                  all, so without this the commonest refusal on this screen was a
                  grey button and no text anywhere. */}
              {/* LOOK BEFORE SENDING (FR-71, UX-DR-15). Every other surface on
                  this page shows the quote as the DESK sees it — cost, margin,
                  the vendor behind each line — and this is the only place it is
                  shown as the CLIENT will read it, before an email leaves
                  permanently.

                  Deliberately NOT gated on `sendable`. A blocked quote is
                  precisely when somebody wants to look at the document to find
                  out what is wrong, and the sheet shows the refusal above an
                  accurate preview rather than withholding both.

                  It stood beside Send on the builder's last step and nowhere
                  else, so removing the builder would have taken the only look at
                  the paper with it. */}
              <Button
                variant="secondary"
                iconLeft={Eye}
                onClick={() => setPreviewOpen(true)}
              >
                {t('quotes.actions.preview')}
              </Button>
              <Button
                iconLeft={Send}
                disabled={send.isPending}
                blockedReason={sendBlockedReason({ sendable, hasCargo, blocked }, t)}
                onClick={() => setSendOpen(true)}
              >
                {t('quotes.actions.sendOffer')}
              </Button>
            </>
          }
        />

        <QuotePageColumns
          /* The rail the builder has, on the surface that had none. Read-only and
             already-loaded: every card takes the record this page fetched and
             adds no request.

             MONEY FIRST, and it is the one card here that repeats a figure from
             the tabs below. The cost, the customer total and the margin are what
             the quote is FOR, and on this page they used to be reachable only by
             opening the costing tab and scrolling to MarginPanel. That panel
             stays where it is: it is where the numbers are CHANGED, with the
             markup field and its Apply button. The rail is where they are SEEN,
             beside every tab. Neither surface counts anything — both print
             `record.costTotals` — so the duplication cannot become a
             disagreement. */
          aside={
            <>
              <QuoteMoneyCard record={record} />
              <QuoteOfferFactsCard record={record} />
              {/* Readiness is a question about a quote that has not gone out. On
                  a sent or converted record every line is a tick by construction,
                  which is a card spent saying nothing. */}
              {record.status === 'draft' ? (
                <QuoteReadinessCard record={record} />
              ) : null}
            </>
          }
        >
          {blocked ? (
            <Banner
              title={t('quotes.checklist.blocked', { count: checklist.length })}
              action={
                /* A sent quote's cargo is not editable, so the banner states the
                   gap without offering a fix that would refuse. */
                record.status === 'draft' ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      // The tab first: the sheet belongs to the Cargo panel, and
                      // closing it from any other tab would drop the reader on a
                      // section that has nothing to do with what they just fixed.
                      const next = new URLSearchParams(searchParams)
                      next.set('tab', 'cargo')
                      setSearchParams(next, { replace: true })
                      setCargoTarget(firstBlocked)
                    }}
                  >
                    {t('quotes.actions.fixCargo')}
                  </Button>
                ) : undefined
              }
            >
              <ul className="mt-0.5 flex flex-col gap-0.5">
                {checklist.map((issue) => (
                  <li key={issue.id}>{issueText(issue, t)}</li>
                ))}
              </ul>
            </Banner>
          ) : !hasCargo ? (
            <Banner tone="info" title={t('quotes.cargo.emptyTitle')}>
              {t('quotes.cargo.emptyDescription')}
            </Banner>
          ) : (
            <Banner tone="info" title={t('quotes.cargo.readyTitle')}>
              <span className="flex items-center gap-1.5">
                <Check size={13} aria-hidden />
                {t('quotes.cargo.readyDescription')}
              </span>
            </Banner>
          )}

          {/* A refused reopen is reported beside the button that asked for it. The
              send's own refusal stays INSIDE the sheet, which is still open on a
              failure with the choice intact. */}
          {reopen.error ? (
            <Banner tone="negative" title={errorMessage(reopen.error, t)} />
          ) : null}

          {/* Approve is reported the same way, and must be: it is the one button
              here that NAVIGATES on success, so a silent failure leaves the user on
              an unchanged page with no case and nothing said — indistinguishable
              from a dead button. The server refuses it for ordinary reasons (a
              quote already converted, a costing that cannot be booked, a user
              without QuoteSend), and each of those is a sentence worth reading. */}
          {convert.error ? (
            <Banner tone="negative" title={errorMessage(convert.error, t)} />
          ) : null}

          {/* The sections are tabs, not a stack (UX-DR-9): a quote carries six of
              them and a reader scrolling past five cards to reach the revisions
              loses the header — including the checklist banner and the Send button
              that the banner is about. One panel at a time keeps the decision and
              the evidence for it on the same screen.

              The cargo sheet is reachable from the checklist banner ABOVE this
              switcher, so pressing "Fix cargo" from the Terms tab has to land the
              reader where the fix happens. Opening the sheet also selects the tab
              behind it — otherwise the sheet closes onto the wrong section. */}
          <TabNav
            label={t('quotes.sectionsLabel')}
            items={TABS}
            value={tab}
            onChange={(key) => {
              const next = new URLSearchParams(searchParams)
              next.set('tab', key)
              setSearchParams(next, { replace: true })
            }}
          />

          <TabPanel tabKey={tab}>
            {tab === 'cargo' ? (
              <CargoSection
                record={record}
                target={cargoTarget}
                setTarget={setCargoTarget}
              />
            ) : null}

            {/* The costing and the one thing done TO a costing here: it is priced.
                The tab mounted only the editor, so this surface could change what
                every line costs and could not put a margin on any of it — a cost
                ledger rather than a quotation. The order is the builder's pricing
                step, deliberately: a reader who learned it there does not have to
                relearn it here, and the two surfaces are the same work seen twice.

                The pricing desk — the FR-49 ask that sends `reason: 'pricing'` to a
                colleague — is NOT mounted here: the tab is the costing and its
                markup, and the ask is a piece of collaboration rather than a figure
                on the sheet. `PricingDeskCard` still exists and still carries that
                behaviour; nothing on this page renders it. */}
            {tab === 'pricing' ? (
              <div className="flex flex-col gap-4">
                <CostLineSection
                  record={record}
                  target={costLineTarget}
                  setTarget={setCostLineTarget}
                />
                <MarginPanel record={record} />
              </div>
            ) : null}

            {tab === 'routes' ? <RouteOptionsSection record={record} /> : null}
            {/* The INTERNAL team, and now only that (Story 2.5, FR-14, FR-69).

                FR-69 asks for assignment "in the record header where ownership is
                displayed, not on a settings screen", and the case side satisfies
                that literally by putting CaseTeamSection under the overview beside
                "Responsible". A quote has no overview tab — its ownership is the
                `Pricing:` line in the sticky header, which is a text run and not
                somewhere a card with a picker can live. So this is the nearest
                honest placement: on the record, one click from the header, on the
                tab that already answers "who is on this deal".

                The EXTERNAL parties (`QuoteParticipantsSection`, FR-24) used to sit
                below this card and are no longer mounted, on request. The
                component, its sheet and its writes are untouched; nothing renders
                them, so a quote's parties are named on the case after conversion
                rather than here. The two sections were never to be unified — a
                colleague has a user id and a role identity owns, a participant has
                a shipping role and no account — so the one that stayed keeps its
                own vocabulary rather than absorbing the other's. */}
            {tab === 'participants' ? <QuoteTeamSection record={record} /> : null}
            {/* `editable`: this page asks the deal terms NOWHERE else, so the card
              carries the fields rather than a read-out of them. The builder passes
              nothing — its pricing step already asks every one of them one card up,
              and two editors for one field is the drift the sections exist to
              prevent. The SERVER still decides whether the window is open; see
              TermsSection. */}
          {tab === 'terms' ? <TermsSection record={record} editable /> : null}
            {tab === 'revisions' ? <RevisionsSection record={record} /> : null}
            {/* `enabled` rather than an unmounted hook: the query is declared on
                every render so React's hook order is stable, and only its FETCH is
                gated on the tab being open. */}
            {tab === 'history' ? (
              <QuoteHistorySection quoteId={quoteId} enabled={tab === 'history'} />
            ) : null}
          </TabPanel>
        </QuotePageColumns>

        {/* The preview can SEND, because the point of looking is to then act —
            and it hands off to the same sheet rather than sending on its own, so
            the recipient is still named before the mail leaves. */}
        <OfferPreviewSheet
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          preview={preview.data}
          pending={preview.isPending}
          error={preview.error}
          onRetry={() => void preview.refetch()}
          onSend={() => {
            setPreviewOpen(false)
            setSendOpen(true)
          }}
          /* A boolean here, not the sentence: the sheet's own footer words the
             refusal. The page's Send button carries the sentence because it is
             the control a reader hovers to ask why. */
          sendBlocked={Boolean(sendBlockedReason({ sendable, hasCargo, blocked }, t))}
          sendPending={send.isPending}
          language={offerLanguage}
          onLanguageChange={setOfferLanguage}
        />

        <OfferSendSheet
          open={sendOpen}
          onClose={() => {
            setSendOpen(false)
            send.reset()
          }}
          quote={record}
          /* The language the preview was read in, not a default of this sheet's
             own. Whoever opened the preview has already checked a page in one
             language; offering them a control that starts somewhere else is how
             an English offer gets issued in Russian by a reader who never touched
             the field. */
          language={offerLanguage}
          onLanguageChange={setOfferLanguage}
          onSubmit={(values) =>
            send.mutate(values, {
              // Closed only on success. A refusal leaves the sheet open with the
              // message on it, because the user has to decide what to do next —
              // and a sheet that vanished on failure would read as a send.
              onSuccess: () => setSendOpen(false),
            })
          }
          pending={send.isPending}
          /* sendErrorMessage rather than the generic one: a send refused because
             the issuing entity has no legal footer arrives as a validation
             problem on `issuingEntity`, a field this sheet does not render, so
             the generic wording would say "check the highlighted fields" of a
             form that has none. The fix is in Settings → Entities and only that
             sentence sends the reader there. */
          submitError={send.error ? sendErrorMessage(send.error, t) : undefined}
        />

        {/* The header editor (FR-4). Reuses the sheet the wizard's step 1 was
            built from rather than a third form asking the same questions: two
            copies of these fields would eventually disagree about whether "20 "
            is a number, one surface sending what the other refused.

            toQuoteUpdatePayload, NOT toQuotePayload. The PUT takes the whole
            record and reads an absent key as "clear it", and QuoteDraft carries
            only about half the wire's fields — so the create builder would have
            wiped the cargo owner, the pickup location, the carrier line and the
            FR-11 terms on the first save. That function starts from the record and
            overlays the draft, which is the only safe shape for a whole-record PUT
            reached from a partial form. */}
        <QuoteFormSheet
          open={editing}
          onClose={() => {
            setEditing(false)
            update.reset()
          }}
          record={record}
          pending={update.isPending}
          onSubmit={(values) =>
            update.mutate(
              { id: quoteId, payload: toQuoteUpdatePayload(record, values) },
              // Closed only on success, exactly as the send sheet is: a refusal
              // leaves the sheet open holding what was typed, because UX-DR-18
              // forbids a failed request discarding the user's work.
              { onSuccess: () => setEditing(false) },
            )
          }
        />
      </QuotePage>

      {/* The floating dock at lg and up, the full-screen view below it — see
          ChatDock for why the two are one mount, and for why the dock may be
          dismissed now when the column it replaced could not be. Fixed in both
          shapes, so it takes part in no layout here.

          The number and the client are the dock's subtitle, off the record this
          page already holds. `unread` is the same count the header badge
          renders, from the same conversation row: the badge and the dock's
          minimised button must never disagree about whether somebody spoke.

          allowAttachments is FALSE: there is no quote file store, so a
          paperclip would upload bytes no message could ever point at.
          `archived` comes off the conversation row — a converted quote's thread
          stays readable and stops accepting messages, because the conversation
          continues on the case. */}
      {canChat ? (
        <ChatDock
          kind="quote"
          subjectId={record.id}
          conversationId={conversation?.id}
          subjectNumber={record.number}
          subjectClient={record.clientName}
          unread={conversation?.unread ?? 0}
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          allowAttachments={false}
          archived={conversation?.archived}
        />
      ) : null}
    </div>
  )
}
